import { useHistory } from "react-router-dom";
import {
  navigateToIntegrationBuilderExistingLinkingFlowStepPathSetup,
  navigateToIntegrationBuilderNewLinkingFlowStepPathSetup,
} from "../../../../../router/RouterUtils";
import {
  LinkingFlowStepPathIntegrationBuilder,
  LinkingFlowStepPathTableRowsIntegrationBuilder,
  LinkingFlowStepPathTypeEnums,
} from "../../../utils/Entities";
import { formattedPathTypeName } from "../../utils/helpers";
import { useState } from "react";
import { Button, ButtonVariant, Dialog, Text, Toggle } from "@merge-api/merge-javascript-shared";
import useCreateOrPatchLinkingFlowStepPath from "../../hooks/useCreateOrPatchLinkingFlowStepPath";
import { ChevronRight, Plus } from "lucide-react";

type LinkingFlowStepPathsTableRowProps = {
  linkingFlowStepPath: LinkingFlowStepPathIntegrationBuilder | undefined;
  integrationID: string;
  tableRow: LinkingFlowStepPathTableRowsIntegrationBuilder;
  reloadLinkingFlowStepPathsAndAuthConfigs: () => void;
};

const LinkingFlowStepPathsTableRow = ({
  integrationID,
  linkingFlowStepPath,
  tableRow,
  reloadLinkingFlowStepPathsAndAuthConfigs,
}: LinkingFlowStepPathsTableRowProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const history = useHistory();

  // Initiate Patch actions
  const { patchLinkingFlowStepPath, isLoadingStepPathUpdate } = useCreateOrPatchLinkingFlowStepPath(
    {
      setIsModalOpen: setIsModalOpen,
      integrationID: integrationID,
      linkingFlowStepPathID: tableRow.step_path_id,
      requestedLinkingFlowStepPath: linkingFlowStepPath
        ? {
            ...linkingFlowStepPath,
            is_active: !linkingFlowStepPath.is_active,
          }
        : undefined,
      reloadLinkingFlowStepPathsAndAuthConfigs: reloadLinkingFlowStepPathsAndAuthConfigs,
    }
  );

  // Define button actions
  const onRowClick = () => {
    if (tableRow.step_path_id) {
      navigateToIntegrationBuilderExistingLinkingFlowStepPathSetup(
        history,
        integrationID,
        tableRow.step_path_id
      );
    }
  };

  const onButtonClick = () => {
    if (tableRow) {
      navigateToIntegrationBuilderNewLinkingFlowStepPathSetup(
        history,
        integrationID,
        tableRow.auth_config_id,
        tableRow.path_type
      );
    }
  };

  const onToggleClick = () => {
    setIsModalOpen(true);
  };

  return linkingFlowStepPath ? (
    <tr className="cursor-pointer h-[74px]" onClick={onRowClick}>
      <td>
        <Text>{tableRow.auth_config_name}</Text>
      </td>
      <td>
        <Text>{tableRow.path_type ? formattedPathTypeName(tableRow.path_type) : ""}</Text>
      </td>
      <td>
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Toggle
            onChange={onToggleClick}
            checked={linkingFlowStepPath.is_active}
            label={linkingFlowStepPath.is_active ? "Active" : "Inactive"}
            labelPlacement="right"
          />
        </div>
      </td>
      <td className="flex justify-end">
        <Button variant={ButtonVariant.IconShadowHidden} size="sm">
          <ChevronRight size={16} className="text-gray-40" />
        </Button>
      </td>
      <Dialog
        title={
          linkingFlowStepPath.is_active
            ? "Unpublish linking flow steps"
            : "Publish linking flow steps"
        }
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        primaryButtonVariant={
          linkingFlowStepPath.is_active ? ButtonVariant.DangerFilled : ButtonVariant.PrimaryGray
        }
        primaryButtonText={linkingFlowStepPath.is_active ? "Unpublish" : "Publish"}
        onPrimaryButtonClick={() => patchLinkingFlowStepPath()}
        primaryButtonLoading={isLoadingStepPathUpdate}
        onSecondaryButtonClick={() => setIsModalOpen(false)}
      >
        <div className="flex flex-col gap-y-2">
          {linkingFlowStepPath.is_active ? (
            <>
              <Text>
                Unpublishing will remove these linking flow steps for all end users of this
                integration.
              </Text>

              <Text>Are you sure you want to unpublish?</Text>
            </>
          ) : (
            <>
              <Text>
                Publishing will make these linking flow steps appear for all end users of this
                integration.
              </Text>
              <Text>Are you sure you want to publish?</Text>
            </>
          )}
        </div>
      </Dialog>
    </tr>
  ) : (
    <tr className="h-[74px]">
      <td>
        <Text>{tableRow.auth_config_name}</Text>
      </td>
      <td colSpan={3}>
        <Button onClick={onButtonClick} size="sm" leftIcon={<Plus size={12} />}>
          {tableRow.path_type === LinkingFlowStepPathTypeEnums.PATH_TYPE_SCRAPER
            ? "Scraper steps"
            : tableRow.path_type === LinkingFlowStepPathTypeEnums.PATH_TYPE_SCRAPER_MFA
            ? "Scraper MFA steps"
            : tableRow.path_type ===
              LinkingFlowStepPathTypeEnums.PATH_TYPE_SCRAPER_SECURITY_QUESTION
            ? "Scraper security question steps"
            : "Linking flow steps"}
        </Button>
      </td>
    </tr>
  );
};

export default LinkingFlowStepPathsTableRow;
